
import { supabase } from '@/lib/supabase/client';
import { Product } from '@/lib/supabase/products/types';
import { setOrganizationIdCache } from '@/lib/supabase/products/organization';

export const fetchProductFromAllProducts = async (
  productSlug: string,
  organizationId?: string | null,
  setOrganizationId?: (id: string | null) => void
): Promise<Product | null> => {
  try {
    console.log("Fetching product from all products:", productSlug);
    
    // Extract UUID if present in the slug (format: name-id)
    const uuidRegex = /-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
    const match = productSlug.match(uuidRegex);
    
    if (match && match[1]) {
      console.log("UUID extracted from slug:", match[1]);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', match[1])
        .single();
      
      if (!error && data) {
        console.log("Product found by UUID:", data.name);
        
        if (data.organization_id && setOrganizationId) {
          console.log("Setting organization ID from product:", data.organization_id);
          setOrganizationId(data.organization_id);
          setOrganizationIdCache(data.organization_id);
        }
        
        return data as Product;
      }
    }
    
    // If we have an organization ID, try finding by slug within org
    if (organizationId) {
      console.log("Trying to find product by organization:", organizationId);
      
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        const normalizedSlug = productSlug.toLowerCase().trim();
        
        // Find product with matching slug
        const product = data.find(p => {
          // Create normalized slug from product name
          const productNameSlug = p.name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
          
          return productNameSlug.includes(normalizedSlug) || normalizedSlug.includes(productNameSlug);
        });
        
        if (product) {
          console.log("Product found by name slug in organization:", product.name);
          return product as Product;
        }
      }
    }
    
    // Last resort - try all products regardless of organization
    console.log("Trying to find product across all products");
    
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error("Error fetching all products:", error);
      return null;
    }
    
    const normalizedSlug = productSlug.toLowerCase().trim();
    
    // Find product with matching slug
    const product = data.find(p => {
      // Create normalized slug from product name
      const productNameSlug = p.name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      
      return productNameSlug.includes(normalizedSlug) || normalizedSlug.includes(productNameSlug);
    });
    
    if (product) {
      console.log("Product found by name slug across all products:", product.name);
      
      if (product.organization_id && setOrganizationId) {
        console.log("Setting organization ID from product:", product.organization_id);
        setOrganizationId(product.organization_id);
        setOrganizationIdCache(product.organization_id);
      }
      
      return product as Product;
    }
    
    return null;
  } catch (error) {
    console.error("Exception fetching product from all products:", error);
    return null;
  }
};
